import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { colonyApi, setColonyAuthToken } from "@/lib/colonyApi";
import { formatErr } from "@/lib/api";
import { logError } from "@/lib/log";

/**
 * Colony portal auth — INTENTIONALLY SEPARATE from the pool AuthContext.
 * Same browser can hold a pool session and a colony session at once
 * because the two portals use different cookies + different collections.
 */
const ColonyAuthContext = createContext(null);

export function ColonyAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const identityVersion = useRef(0);

  const checkAuth = useCallback(async () => {
    const version = ++identityVersion.current;
    try {
      const { data } = await colonyApi.get("/colony/auth/me");
      if (version === identityVersion.current) setUser(data?.user || null);
    } catch {
      if (version === identityVersion.current) setUser(null);
    } finally {
      if (version === identityVersion.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip the auto-probe when returning from Google OAuth — the callback
    // page will hydrate the session explicitly.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
    return () => { identityVersion.current += 1; };
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await colonyApi.post("/colony/auth/login", { email, password });
      identityVersion.current += 1;
      setColonyAuthToken(data.token);
      setUser(data.user);
      setLoading(false);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatErr(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    identityVersion.current += 1;
    try { await colonyApi.post("/colony/auth/logout"); } catch (e) { logError("Colony logout failed:", e); }
    identityVersion.current += 1;
    setColonyAuthToken(null);
    setUser(null);
    setLoading(false);
  };

  return (
    <ColonyAuthContext.Provider value={{ user, loading, login, logout, refresh: checkAuth }}>
      {children}
    </ColonyAuthContext.Provider>
  );
}

export const useColonyAuth = () => useContext(ColonyAuthContext);
